import React from "react";
import {
  usePlayer,
  useGame,
  useRound,
  useStage,
} from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";
import {
  getPreLobbyMode,
  usesPostPeerAskAllOpenEntry,
} from "../utils/conditionUtils.js";
import {
  formatPredictionValue,
  normalizePredictionValue,
} from "../components/PredictionButtonPage.jsx";
import {
  PeerRationalesPanel,
  useStaticSyntheticPeers,
} from "../components/PeerRationalesPanel.jsx";
import {
  POST_PEER_REFLECTION_KEYS,
  ReflectionAssistantChat,
  ReflectionTemplateQuestionnaire,
} from "../components/ReflectionActivity.jsx";
import {
  ReflectionAssistantChatAskAll,
  ReflectionTemplateQuestionnaireAskAll,
} from "../components/ReflectionActivityAskAll.jsx";

// The reflection activity, run after the peer-review stage. It is the same
// instrument as the pre-lobby version: identical questions, identical coach
// prompt, identical continue gate. Only the storage keys differ.
export function PostPeerReflection() {
  const player = usePlayer();
  const game = useGame();
  const round = useRound();
  const stage = useStage();
  const treatment = game?.get("treatment") || {};
  const taskConfig = getTaskConfig(treatment);
  const preLobbyMode = getPreLobbyMode(treatment);
  const peers = useStaticSyntheticPeers({ game, round, stage });

  // The shared chat component sets postPeerAssistantCompleted / ...At before
  // calling this, mirroring how the pre-lobby version marks completion.
  const handleComplete = () => {
    const timestamp = Date.now();
    player.stage.set("submittedAt", timestamp);
    player.stage.set("submit", true);
  };

  // Both arms can consult the peer responses while working through the
  // activity, so the two post-peer arms differ only in the activity itself.
  const peerPanel = (
    <PeerRationalesPanel
      peers={peers}
      formatBelief={(value) => formatPredictionValue(value, taskConfig)}
      subtitle="Reopen the two other participants' responses while you answer."
    />
  );

  // The activity asks the participant to explain the reason behind their
  // prediction, so remind them which prediction that is: whether they held or
  // moved after the peer review, and to what. Shown in both arms so the two
  // post-peer conditions stay symmetric.
  const priorPrediction =
    player.get("preLobbyPredictionBeforeAI") ??
    player.get("preLobbyPrediction") ??
    (game.get("playerBeliefs") || {})[player.id]?.initialBelief;
  const postPeerPrediction = player.get("postPeerPrediction");
  const normalizedPrior = normalizePredictionValue(priorPrediction, taskConfig);
  const normalizedPostPeer = normalizePredictionValue(postPeerPrediction, taskConfig);
  const formattedPrior = formatPredictionValue(priorPrediction, taskConfig);
  const formattedPostPeer = formatPredictionValue(postPeerPrediction, taskConfig);
  const keptEstimate = normalizedPrior !== "" && normalizedPrior === normalizedPostPeer;
  const beliefReminder = normalizedPostPeer === "" ? null : (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <p className="text-base text-blue-900">
        {keptEstimate ? (
          <>
            After reading the two other participants' responses, you kept your
            estimate at <strong>{formattedPostPeer}</strong>.
          </>
        ) : normalizedPrior === "" ? (
          <>
            After reading the two other participants' responses, your estimate is{" "}
            <strong>{formattedPostPeer}</strong>.
          </>
        ) : (
          <>
            After reading the two other participants' responses, you changed your
            estimate from <strong>{formattedPrior}</strong> to{" "}
            <strong>{formattedPostPeer}</strong>.
          </>
        )}
      </p>
    </div>
  );

  // The askall pair targets all three rationales (own answer + both peers):
  // three sequential mini-conversations in the Socratic arm, six template
  // boxes in the Template arm. There is no post-peer estimate stage in these
  // conditions, so the own answer under discussion is the initial estimate.
  const postPeerAskAll =
    treatment?.postPeerAskAll === true ||
    treatment?.factors?.postPeerAskAll === true;
  // nocounter conditions drop the counter-argument from both arms: the
  // "what would someone who disagrees say" box in the Template arm, and the
  // corresponding aims and question category in the Socratic coach prompt
  // (applied server-side in generateCalibrationCoachPrompt).
  const postPeerNoCounter =
    treatment?.postPeerNoCounter === true ||
    treatment?.factors?.postPeerNoCounter === true;
  // The ask-all follow-up (postPeerAskAllOpenEntry) keeps the post-peer
  // estimate stage, so the own answer under discussion is the estimate given
  // right after the peer review, as in the group-first arms. If that stage
  // timed out without an estimate, the initial estimate is still the
  // participant's current answer, so it stays the fallback.
  const postPeerAskAllOpenEntry = usesPostPeerAskAllOpenEntry(treatment);
  const askAllOwnPrediction =
    postPeerAskAllOpenEntry && normalizedPostPeer !== ""
      ? postPeerPrediction
      : priorPrediction;
  const normalizedAskAllOwn = normalizePredictionValue(askAllOwnPrediction, taskConfig);
  const formattedAskAllOwn = formatPredictionValue(askAllOwnPrediction, taskConfig);
  const orderedPeers = [...peers].sort(
    (a, b) => (Number(a?.displayPosition) || 0) - (Number(b?.displayPosition) || 0)
  );

  // One random order over the three discussions (own answer included), drawn
  // once per participant and stored, so refreshes and the export see the same
  // order. Tokens are "own" plus the two synthetic peer ids.
  const askAllWindowOrder = React.useMemo(() => {
    if (!postPeerAskAll) {
      return null;
    }
    const stored = player.get("postPeerAskAllWindowOrder");
    if (Array.isArray(stored) && stored.length > 0) {
      return stored;
    }
    if (orderedPeers.length < 2) {
      return null;
    }
    const tokens = ["own", ...orderedPeers.map((peer) => String(peer.id))];
    for (let i = tokens.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [tokens[i], tokens[j]] = [tokens[j], tokens[i]];
    }
    player.set("postPeerAskAllWindowOrder", tokens);
    return tokens;
  }, [postPeerAskAll, orderedPeers.length]);

  // Publish the own answer once so the server-side coach prompt and the UI
  // quote the same value.
  React.useEffect(() => {
    if (!postPeerAskAll || normalizedAskAllOwn === "") {
      return;
    }
    const existing = player.get("postPeerAskAllOwnAnswer");
    if (existing === undefined || existing === null || String(existing) === "") {
      player.set("postPeerAskAllOwnAnswer", normalizedAskAllOwn);
    }
  }, [postPeerAskAll, normalizedAskAllOwn]);

  if (postPeerAskAll) {
    const formatBelief = (value) => formatPredictionValue(value, taskConfig);
    const askAllEntries = (askAllWindowOrder || [])
      .map((token) =>
        token === "own"
          ? { kind: "own" }
          : {
              kind: "peer",
              peer: orderedPeers.find((peer) => String(peer.id) === String(token)),
            }
      )
      .filter((entry) => entry.kind === "own" || entry.peer);
    if (!askAllWindowOrder || askAllEntries.length === 0) {
      return (
        <div className="max-w-4xl mx-auto p-6 text-gray-600">
          Loading the discussion setup...
        </div>
      );
    }
    // The reminder quotes the initial estimate: with no post-peer estimate
    // stage, that is the participant's current answer going into the activity.
    // The ask-all follow-up has that stage and a typed final answer, so it
    // shows the group-first reminder, which says whether the participant held
    // or moved after the peer review.
    const openEntryReminder =
      beliefReminder ??
      (normalizedAskAllOwn === "" ? null : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-base text-blue-900">
            Your estimate is <strong>{formattedAskAllOwn}</strong>.
          </p>
        </div>
      ));
    const askAllReminder = postPeerAskAllOpenEntry
      ? openEntryReminder
      : normalizedPrior === "" ? null : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-base text-blue-900">
            Your estimate is <strong>{formattedPrior}</strong>. After this
            activity, you will give your final answer by choosing among the
            answers: keep your estimate, or adopt one of the two other
            participants' answers.
          </p>
        </div>
      );
    if (preLobbyMode === "template") {
      return (
        <ReflectionTemplateQuestionnaireAskAll
          next={handleComplete}
          taskConfig={taskConfig}
          keys={POST_PEER_REFLECTION_KEYS}
          entries={askAllEntries}
          ownAnswer={askAllOwnPrediction}
          formatBelief={formatBelief}
          contextPanel={peerPanel}
          beliefReminder={askAllReminder}
          noCounter={postPeerNoCounter}
        />
      );
    }
    return (
      <ReflectionAssistantChatAskAll
        next={handleComplete}
        predictionForOpener={askAllOwnPrediction}
        taskConfig={taskConfig}
        keys={POST_PEER_REFLECTION_KEYS}
        entries={askAllEntries}
        ownAnswer={askAllOwnPrediction}
        formatBelief={formatBelief}
        contextPanel={peerPanel}
        beliefReminder={askAllReminder}
      />
    );
  }

  if (preLobbyMode === "template") {
    return (
      <ReflectionTemplateQuestionnaire
        next={handleComplete}
        taskConfig={taskConfig}
        keys={POST_PEER_REFLECTION_KEYS}
        contextPanel={peerPanel}
        beliefReminder={beliefReminder}
        showDetailsToggle
      />
    );
  }

  return (
    <ReflectionAssistantChat
      next={handleComplete}
      predictionForOpener={postPeerPrediction}
      taskConfig={taskConfig}
      keys={POST_PEER_REFLECTION_KEYS}
      contextPanel={peerPanel}
      beliefReminder={beliefReminder}
      showDetailsToggle
    />
  );
}
